import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const tickerCrawlStripSkill: CodeoSkill = {
    name: SKILL_NAMES.tickerCrawlStrip,
    kind: 'codeo',
    description:
        'Crawl a strip of short items horizontally across the frame at constant speed — for a ' +
        'list too long to show at once, a run of logos or figures, or a broadcast-news register.',
    documentUsesSkill: check(
        (script, markup) =>
            /white-space:\s*nowrap|\bwhitespace-nowrap\b/.test(markup) &&
            /ease:\s*['"]none/.test(script) &&
            /\bx:\s*-/.test(script),
    ),
    instructions: `# Ticker Crawl Strip

A band of short items slides steadily across the frame, at constant speed, with no easing. It reads as a feed: more items exist than the frame can hold, and they keep coming. Use it when the *quantity* is the point — every integration, every customer, every city — not when three items would have said it better.

The crawl is one linear tween on a wide track. Everything else (the band arriving, a label riding in front of it) is ordinary staggered entrance.

## Markup

The track is a flex row wider than the frame; the band clips it. Give the track no wrapping so items stay on one line.

\`\`\`html
<div class="absolute inset-0 flex flex-col justify-end pb-24">
  <div id="ticker" class="w-full flex items-center overflow-hidden" style="height: 96px; background: var(--l5-color-1, #101820)">
    <div id="ticker-label" class="flex-none flex items-center" style="z-index: 2; height: 96px; padding: 0 32px; background: var(--l5-color-2, #38bdf8); font-family: var(--l5-font-0), sans-serif; font-size: 30px; font-weight: 800; letter-spacing: 0.12em; color: var(--l5-color-1, #101820)">LIVE IN</div>
    <div id="ticker-track" class="flex-none flex items-center gap-16 whitespace-nowrap" style="padding-left: 64px; font-family: var(--l5-font-0), sans-serif; font-size: 36px; font-weight: 600; color: var(--l5-color-0, #ffffff)">
      <span>Toronto</span><span>Berlin</span><span>Lagos</span><span>São Paulo</span><span>Jakarta</span><span>Lisbon</span><span>Seoul</span><span>Austin</span>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

Measure the track rather than guessing: the crawl distance is how far it must travel for its tail to clear the band.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const trackEl = document.querySelector('#ticker-track');
const bandEl = document.querySelector('#ticker');
const travel = trackEl.scrollWidth - bandEl.clientWidth + 200;

gsap.set('#ticker', { y: 120 });
gsap.set('#ticker-track', { x: 0 });

tl.to('#ticker', { y: 0, duration: 0.5, ease: 'power4.out' }, 0);
tl.to('#ticker-track', { x: -travel, duration: 5.4, ease: 'none' }, 0.45);
tl.to('#ticker', { y: 120, duration: 0.36, ease: 'power3.in' }, 6.1);

window.__timelines = { main: tl };
\`\`\`

\`ease: 'none'\` is the whole point — any easing makes the feed appear to speed up or stall, which reads as a broken loop rather than a constant stream.

## Adapt

- **Speed**: tune \`duration\`, not \`travel\`. Roughly 200–260 px/s reads as legible; faster becomes texture, slower becomes a stall.
- **Direction**: crawl right-to-left for reading languages that run that way; flip the sign on \`x\` and the track's \`padding\` side.
- **No label**: drop \`#ticker-label\` for a plain band. Keep the label when the strip needs a category to make sense of it.
- **Logos instead of words**: swap the spans for inline \`<svg>\` marks at a fixed height. Same crawl.
- **Top of frame**: swap the root's \`justify-end pb-24\` for \`justify-start pt-24\` and enter from \`y: -120\` — useful when the lower third of the frame is already spoken for.
- **Full-bleed type**: at 120px+ the same crawl becomes a statement rather than a feed; drop the band background and let the words run across the composition.

## Pitfalls

- Duplicate enough items that the track is genuinely wider than the frame. A track that runs out mid-crawl leaves a growing empty gap, which is the tell that this is a tween and not a feed.
- Never express the crawl as an infinite repeat — the host seeks the timeline to render each frame, which needs a finite duration. One long linear tween covers the whole scene.
- \`scrollWidth\` is read once at build time; that is fine here because the content is static. Don't re-measure inside \`onUpdate\`.
- The track is \`x\`-tweened, so it must not carry a CSS \`transform\`. Position it with flex and padding.
- Don't crawl and animate the items individually at the same time — pick the feed or pick the stagger, not both.
- Anything a viewer must actually read should not be crawling. Put the payload in a still headline and let the ticker carry the volume.`,
};
